/**
 * ページ背景PDF管理カスタムフック
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import Log from '@/Logger';

/**
 * ページ背景管理フックのオプション
 */
export interface UsePageBackgroundOptions {
    docId: string;
    currentPage: { id: string; background?: string } | undefined;
    getDocumentFile: (docId: string, fileId: string) => Promise<Blob>;
}

/**
 * ページ背景管理フックの戻り値
 */
export interface UsePageBackgroundReturn {
    pageBackground: string | null; // null=取得中、""=背景なし、string=BlobURL
    clearCache: () => void;        // キャッシュクリア関数
    error: string | null;          // エラーメッセージ
}

/**
 * ページ背景PDF の管理を担うカスタムフック
 * 
 * @param options 設定オプション
 * @returns ページ背景管理フックの戻り値
 */
export function usePageBackground(options: UsePageBackgroundOptions): UsePageBackgroundReturn {
    const { docId, currentPage, getDocumentFile } = options;

    // ページ背景のBlobUrl (取得中は null、存在しない場合は '')
    const [pageBackground, setPageBackground] = useState<string | null>(""); // 初期値は空文字（背景なし）

    // 致命エラー状態
    const [error, setError] = useState<string | null>(null);

    // ページ背景のBlobUrlのキャッシュ
    const pageBackgroundCacheRef = useRef<Map<string, string>>(new Map<string, string>());

    // キャッシュクリア関数
    const clearCache = useCallback(() => {
        if (!pageBackgroundCacheRef.current?.size) return;
        const logger = Log.getLogger();
        logger.ui.info("usePageBackground", "clearCache", `${pageBackgroundCacheRef.current?.size} URLs.`);
        Array.from(pageBackgroundCacheRef.current.values()).forEach((url) => {
            // キャッシュしているpdfのrevokeを行う
            URL.revokeObjectURL(url);
        });
        pageBackgroundCacheRef.current.clear();
    }, []);

    // currentPageの背景が変わったらpageBackgroundを更新する
    useEffect(() => {
        if (!currentPage?.id) return;
        const logger = Log.getLogger();

        if (!currentPage?.background) {
            logger.ui.info("usePageBackground", "fetchBackground", `${currentPage?.id} has no background.`);
            setPageBackground(""); // 背景なし
            setError(null); // エラーをクリア
            return;
        }

        // キャッシュに存在する場合はそれを返す
        if (pageBackgroundCacheRef.current.has(currentPage.id)) {
            logger.ui.info("usePageBackground", "fetchBackground", `${currentPage.id} background found in cache.`);
            setPageBackground(pageBackgroundCacheRef.current.get(currentPage.id)!);
            return;
        }

        // まだキャッシュにない場合は新規に取得してキャッシュに保存する
        logger.ui.info("usePageBackground", "fetchBackground", `${currentPage.id} background fetching...`);
        let canceled = false;
        setPageBackground(null); // 取得中はnullにする

        // 非同期で背景PDFを取得してBlobUrlを生成
        getDocumentFile(docId, currentPage.background)
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                pageBackgroundCacheRef.current.set(currentPage.id, url);
                if (!canceled) {
                    logger.ui.info("usePageBackground", "fetchBackground", `${currentPage.id} background fetched.`);
                    setPageBackground(url);
                    // PDF.jsへの背景読み込み完了はonLoadSuccessで検知するのでここではフラグを立てない
                }
            })
            .catch((err) => {
                logger.ui.error("usePageBackground", "fetchBackground", `${currentPage.id} background fetch error: ${err}`);
                setPageBackground(""); // 背景なし
                setError("背景PDFを取得できません。");
            });

        return () => {
            canceled = true;
        };
    }, [getDocumentFile, docId, currentPage?.id, currentPage?.background]);

    // アンマウント時のクリーンアップ
    useEffect(() => {
        return () => {
            clearCache();
        };
    }, [clearCache]);

    return {
        pageBackground,
        clearCache,
        error,
    };
}